<template>
    <v-dialog v-model="modal" :max-width="650" scrollable class="modify-manual-modal">
        <v-card ref="modifyManualModal" :loading="loadingInitial" class="overflow-y-hidden">
            <div class="d-flex flex-wrap">
                <div class="me-md-6 flex-grow-0 flex-shrink-1">
                    <v-card-title>
                        {{ $t('client_price') }}
                        <v-icon class="v-card-close mt-3 me-3" @click="modal = false">mdi-close</v-icon>
                    </v-card-title>
                    <v-card-text>
                        <div class="d-flex mt-2 align-end">
                            <v-form ref="form" v-model="valid">
                                <v-row>
                                    <v-col cols="12" md="3">
                                        <v-text-field
                                            v-model="priceAmount"
                                            :prefix="currency"
                                            variant="outlined"
                                            class="font-weight-bold"
                                            type="number"
                                            density="compact"
                                            :label="`${$t('invoices.amount')}*`"
                                            hide-details
                                            data-cy="price-amount"
                                            :rules="[requiredRule('invoices.amount')]"
                                            @update:model-value="updateCommission"
                                        />
                                    </v-col>
                                    <v-col cols="12" md="3">
                                        <v-text-field
                                            v-model="commissionPercent"
                                            :prefix="'%'"
                                            :label="`${$t('commission_percent')}`"
                                            variant="outlined"
                                            type="number"
                                            density="compact"
                                            persistent-placeholder
                                            hide-details
                                            data-cy="commission-percent"
                                            @update:model-value="updateCommission"
                                        />
                                    </v-col>
                                    <v-col cols="12" md="3">
                                        <v-text-field
                                            v-model="commission"
                                            :prefix="currency"
                                            :label="`${$t('commission')}`"
                                            variant="outlined"
                                            type="number"
                                            density="compact"
                                            hide-details
                                            data-cy="commission"
                                            @update:model-value="updateCommissionPercent"
                                        />
                                    </v-col>
                                </v-row>
                            </v-form>
                        </div>
                    </v-card-text>
                </div>
            </div>
            <v-card-actions class="justify-center py-5">
                <div class="d-flex py-4">
                    <v-btn
                        class="mx-1"
                        variant="flat"
                        color="primary"
                        :disabled="!valid"
                        :loading="loading"
                        data-cy="confirm"
                        @click="confirm"
                    >
                        {{ $t('confirm') }}
                    </v-btn>
                    <v-btn variant="flat" class="mx-1" data-cy="cancel" @click="modal = false">
                        {{ $t('cancel') }}
                    </v-btn>
                </div>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script setup lang="ts">
    const props = defineProps<{
        service: any
    }>()
    const valid = ref<boolean>(true)
    const loading = ref<boolean>(false)
    const loadingInitial = ref<boolean>(false)
    const priceAmount = ref(0)
    const commissionPercent = ref(0)
    const commission = ref(0)
    const currency = ref('USD')

    const modal = defineModel<any>()
    const app = useNuxtApp()
    const {t} = useI18n()

    watch(modal, async () => {
        if (modal.value) {
            const price = clone(props.service).salesTerms.find(({type}) => type === 'CLIENT').price
            priceAmount.value = price.amount || 0
            commissionPercent.value = price?.commissionPercent || 0
            commission.value = price?.commission || 0
            currency.value = price.currency || 'USD'
        }
    })

    const updateCommission = () => {
        const amount = (priceAmount.value * commissionPercent.value) / 100
        commission.value = +amount?.toFixed(2)
    }

    const updateCommissionPercent = () => {
        const amount = (commission.value / priceAmount.value) * 100
        commissionPercent.value = +amount?.toFixed(2)
    }

    const confirm = async () => {
        const newTerm = {
            type: 'CLIENT',
            price: {
                amount: priceAmount.value,
                commission: {
                    amount: commission.value,
                },
            },
        }
        const service = props.service
        try {
            loading.value = true
            await app.$api.manualModification.post({
                processId: service.processId,
                status: service.status.toUpperCase(),
                salesTerm: [newTerm],
                applyClientAdjustedPrice: true,
            })
            await runtimeStore.refreshOrder()
            app.$toast.success(t('success_message.saved_successfully'))
            modal.value = false
        } catch (e) {
            app.$toast.error(t('error_message.save_error'))
        } finally {
            loading.value = false
        }
    }
</script>
